import { forwardRef, type ReactNode, useState } from 'react'
import DatePicker from 'react-datepicker'
import { classNameParser } from '../../../../helpers'
import type { InputProps } from '../../../../types'

import 'react-datepicker/dist/react-datepicker.css'
import '../../../../styles/input.css'
import './style.css'

import Tippy from '@tippyjs/react'
import helpImage from '../../../../images/ic-help.svg'

type DateInputProps = InputProps<
  Date,
  {
    minDate?: Date
    maxDate?: Date
    fieldInfoSize?: 'small' | 'large'
    fieldInfo?: ReactNode
    disableMaxDefaultValue?: boolean
  }
>

const CustomInput = forwardRef((props: any, ref) => {
  return (
    <input
      className={`global--input ${props.value ? 'global--with-value' : ''}`}
      onClick={props.onClick}
      value={props.value}
      type="text"
      readOnly={true}
      placeholder={props.placeholder}
    />
  )
})

export const DateInput = (props: DateInputProps) => {
  const [value, setValue] = useState<Date | undefined>(
    props.disableMaxDefaultValue === true ? props.value : props.value || props.maxDate,
  )
  const [infoTarget, setInfoTarget] = useState<HTMLElement>()
  const [fieldTarget, setFieldTarget] = useState<HTMLElement>()

  const handleValueChange = (date: Date | null) => {
    setValue(date || value)
    props.onChange && props.onChange(date || value)
  }

  return (
    <div
      id={props.id}
      key={props.key}
      className={classNameParser('global--input-container', props.className)}
      style={props.style}
    >
      <div className="flex justify-between items-end">
        {props.label ? (
          <label className="global--input-label" htmlFor={props.id}>
            {props.label}
          </label>
        ) : (
          <div></div>
        )}
        <div className="flex flex-row items-end">
          {props.optional === true ? (
            <p className={`global--input-label font-400 !text-[12px] mr-[4px]`}>Optional field</p>
          ) : null}
        </div>
      </div>
      <Tippy
        reference={fieldTarget}
        triggerTarget={infoTarget}
        disabled={!props.fieldInfo}
        content={
          <div className="global--tooltip-content bg-white w-full rounded-md shadow-2xl px-[16px] py-[4px]">
            {props.fieldInfo}
          </div>
        }
      />
      <div
        ref={(el) => {
          if (el) setFieldTarget(el)
        }}
        className="relative"
      >
        <DatePicker
          className={`global--input ${value ? 'global--with-value' : ''}`}
          calendarClassName="input--date-input-calendar"
          customInput={<CustomInput />}
          dateFormat="yyyy-MM-dd"
          dropdownMode="select"
          maxDate={props.maxDate}
          minDate={props.minDate}
          onMonthChange={handleValueChange}
          onYearChange={handleValueChange}
          onChange={handleValueChange}
          selected={value}
          showMonthDropdown
          showYearDropdown
          placeholderText={props.placeHolder}
          onFocus={(e) => e.target.blur()}
        />
        {props.fieldInfo !== undefined ? (
          <div
            ref={(el) => {
              if (el) setInfoTarget(el)
            }}
            className="absolute right-0 top-0"
          >
            <img src={helpImage.src} alt="help icon" width={48} height={48}></img>
          </div>
        ) : null}
      </div>
      {props.error && <p className="global--input-error">{props.error}</p>}
    </div>
  )
}
